import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { ColorScheme } from '@unifyapps/defs/types/interface';
import { useMemo } from 'react';
import { useSystemColorScheme } from '../../hooks/useSystemColorScheme';
import type { ColorSchemeDetails } from '../stores/app/GlobalAppStateStore/types';
import InterfaceHelper from '../stores/InterfaceStore/helper/InterfaceHelper';

export function useColorSchemeDetails({
  colorScheme,
  interfaceRecord,
}: {
  colorScheme?: ColorScheme;
  interfaceRecord?: InterfaceEntity;
}) {
  const systemColorScheme = useSystemColorScheme();
  const interfaceDefaultColorScheme = InterfaceHelper.getTheme(interfaceRecord)?.defaultScheme;

  const colorSchemeDetails = useMemo<ColorSchemeDetails>(() => {
    const effectiveSystemColorScheme =
      systemColorScheme === 'dark' ? ColorScheme.DARK : ColorScheme.LIGHT;

    if (colorScheme === ColorScheme.SYSTEM) {
      return {
        originalColorScheme: ColorScheme.SYSTEM,
        effectiveColorScheme: effectiveSystemColorScheme,
      };
    }
    if (colorScheme) {
      // colorScheme here is LIGHT or DARK (not SYSTEM due to the check above)
      return {
        originalColorScheme: colorScheme,
        effectiveColorScheme: colorScheme as ColorScheme.LIGHT | ColorScheme.DARK,
      };
    }

    // use interface default color scheme when no colorScheme prop is provided
    const effectiveDefault =
      interfaceDefaultColorScheme !== ColorScheme.SYSTEM
        ? //if interfaceDefaultColorScheme is not set, use LIGHT as default
          (interfaceDefaultColorScheme ?? ColorScheme.LIGHT)
        : effectiveSystemColorScheme;

    return {
      originalColorScheme: interfaceDefaultColorScheme ?? ColorScheme.LIGHT,
      effectiveColorScheme: effectiveDefault as ColorScheme.LIGHT | ColorScheme.DARK,
    };
  }, [colorScheme, systemColorScheme, interfaceDefaultColorScheme]);

  return colorSchemeDetails;
}
